import React from 'react';
import { Label } from '../../components/ui/label';
import { Check, Image, Camera } from '@phosphor-icons/react';
import { CellInput, VariantMediaRow } from './_helpers';

export const VariantTable = ({
  config, sharedFields, toggleSharedField, variants, updateVariant,
  platformSettings, currencySymbol, expandedVariantMedia, setExpandedVariantMedia,
  enabledVariants, minPrice,
}) => (
  <>
    {config.sharedFields && config.sharedFields.map(sf => (
      <div key={sf.key} className="mb-4">
        <Label className="text-xs font-medium text-slate-600 mb-2 block">{sf.label}</Label>
        <div className="flex flex-wrap gap-1.5">
          {sf.options.map(opt => {
            const isOn = (sharedFields[sf.key] || []).includes(opt);
            return (
              <button key={opt} type="button" onClick={() => toggleSharedField(sf.key, opt)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs transition-all ${isOn ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
                data-testid={`shared-${sf.key}-${opt}`}>
                {isOn && <Check size={10} weight="bold" />}
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    ))}

    {/* Variant Table */}
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-xs border-collapse min-w-[600px]" data-testid="variant-table">
        <thead>
          <tr className="bg-slate-50">
            <th className="px-2 py-2 text-left font-semibold text-slate-500 border-b border-slate-200 w-8"></th>
            <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">{config.variantLabel}</th>
            {config.columns.map(col => (
              <th key={col.key} className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">{col.label}</th>
            ))}
            <th className="px-2 py-2 text-left font-semibold text-emerald-600 border-b border-slate-200 whitespace-nowrap">Reg. Price ({currencySymbol})</th>
            <th className="px-2 py-2 text-left font-semibold text-orange-600 border-b border-slate-200 whitespace-nowrap">Holiday ({currencySymbol})</th>
            {platformSettings.seller_variant_media !== false && (
              <th className="px-2 py-2 text-center font-semibold text-blue-600 border-b border-slate-200 whitespace-nowrap">Media</th>
            )}
          </tr>
        </thead>
        <tbody>
          {variants.map((variant, i) => (
            <React.Fragment key={variant._uid || `var-${variant.variant_name || ''}-${i}`}>
              <tr className={`border-b border-slate-100 transition-colors ${variant.enabled ? 'bg-white' : 'bg-slate-50/50'}`}
                data-testid={`variant-row-${i}`}>
                <td className="px-2 py-1.5">
                  <input type="checkbox" checked={variant.enabled}
                    onChange={(e) => updateVariant(i, 'enabled', e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500 cursor-pointer"
                    data-testid={`variant-enable-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <span className={`text-xs font-medium whitespace-nowrap ${variant.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                    {variant[config.variantField]}
                  </span>
                </td>
                {config.columns.map(col => (
                  <td key={col.key} className="px-1 py-1.5">
                    <CellInput col={col} value={variant[col.key]} disabled={!variant.enabled}
                      onChange={(val) => updateVariant(i, col.key, val)} />
                  </td>
                ))}
                <td className="px-1 py-1.5">
                  <input type="number" value={variant.regular_price}
                    onChange={(e) => updateVariant(i, 'regular_price', e.target.value)}
                    disabled={!variant.enabled} placeholder="0"
                    className="w-20 h-7 px-1.5 border border-emerald-200 rounded text-xs bg-white disabled:opacity-40 disabled:bg-slate-50 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
                    data-testid={`variant-reg-price-${i}`} />
                </td>
                <td className="px-1 py-1.5">
                  <input type="number" value={variant.holiday_price}
                    onChange={(e) => updateVariant(i, 'holiday_price', e.target.value)}
                    disabled={!variant.enabled} placeholder="0"
                    className="w-20 h-7 px-1.5 border border-orange-200 rounded text-xs bg-white disabled:opacity-40 disabled:bg-slate-50 focus:border-orange-400 focus:ring-1 focus:ring-orange-200 outline-none"
                    data-testid={`variant-hol-price-${i}`} />
                </td>
                <td className="px-1 py-1.5 text-center">
                  {variant.enabled && platformSettings.seller_variant_media !== false && (
                    <button onClick={() => setExpandedVariantMedia(expandedVariantMedia === i ? null : i)}
                      className={`p-1.5 rounded-lg transition-colors ${expandedVariantMedia === i ? 'bg-blue-100 text-blue-700' : 'hover:bg-slate-100 text-slate-400'} ${(variant.media || []).length > 0 ? 'text-blue-600' : ''}`}
                      data-testid={`variant-media-toggle-${i}`}>
                      <Image size={16} weight={(variant.media || []).length > 0 ? 'fill' : 'regular'} />
                      {(variant.media || []).length > 0 && (
                        <span className="text-[9px] ml-0.5 font-bold">{(variant.media || []).length}</span>
                      )}
                    </button>
                  )}
                </td>
              </tr>
              {expandedVariantMedia === i && variant.enabled && platformSettings.seller_variant_media !== false && (
                <tr className="bg-blue-50/30">
                  <td colSpan={config.columns.length + 5} className="px-4 py-2">
                    <div className="text-[11px] font-medium text-slate-500 mb-1 flex items-center gap-1">
                      <Camera size={12} /> Photos & Videos for {variant[config.variantField]}
                    </div>
                    <VariantMediaRow
                      variantIndex={i}
                      media={variant.media || []}
                      onChange={(newMedia) => updateVariant(i, 'media', newMedia)}
                      disabled={!variant.enabled}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
    {enabledVariants.length > 0 && minPrice > 0 && (
      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1" data-testid="base-price-note">
        <Check size={12} className="text-emerald-500" weight="bold" />
        Base price auto-set to {currencySymbol}{minPrice.toLocaleString()} (lowest regular price)
      </p>
    )}
  </>
);
